import { Builder, Field, From, inc, Schema, use } from '@bonusly/schema';

import { RuleGroup, RuleGroupBuilder } from './groups';
import { DateOnlySerializer } from './serializers';

@Schema()
export class YearAmountMapping {
	@Field()
	@From('yearThreshold')
	public yearThreshold = 0;

	@Field()
	@From('amount')
	public amount = 0;
}

export type CelebrationState = 'active' | 'inactive';

export enum CelebrationType {
	Birthday = 'birthday',
	Custom = 'custom_yearly_date',
	NewHire = 'new_hire',
	Welcome = 'welcome',
	WorkAnniversary = 'work_anniversary',
	Wellness = 'wellness',
}

export enum CelebrationVisibility {
	Public = 'publicly_visible',
	Private = 'privately_visible',
	Team = 'immediate_team',
}

export enum CelebrationWorkAnniversaryPointSetting {
	EveryYearWithSameAmount = 'everyYearWithSameAmount',
	EveryYearWithCustomAmount = 'everyYearWithCustomAmount',
	OnlySpecificYears = 'onlySpecificYears',
}

@Schema()
export class Celebration {
	@Field()
	@From('id')
	public id = '';

	@Field(String)
	@From('name')
	public name = '';

	@Field()
	@From('type')
	public type: CelebrationType = CelebrationType.Welcome;

	@Field()
	@From('giverId')
	public giverId = '';

	@Field(String)
	@From('headline')
	public headline = '';

	@Field()
	@From('amount')
	public amount: number | null = null;

	@Field()
	@From('includeBenefactors')
	public includeBenefactors = false;

	@Field(String)
	@From('reason')
	public reason = '';

	@Field()
	@From('imageUrl')
	public imageUrl: string | null = null;

	@Field()
	@From('iconUrl')
	public iconUrl: string | null = null;

	@Field()
	@From('state')
	public state: CelebrationState = 'active';

	@Field()
	@From('visibility')
	public visibility: CelebrationVisibility = CelebrationVisibility.Public;

	@Field([YearAmountMapping])
	@From('yearAmountMappings')
	public yearAmountMappings: YearAmountMapping[] = [];

	@Field(RuleGroup)
	@From('receivingRulesGroup')
	public receivingRulesGroup: RuleGroup | null = null;

	@Field(DateOnlySerializer)
	@From('celebrationDate')
	public celebrationDate: Date | null = null;

	get editPath() {
		return `/admin/programs/celebrations/${this.id}/edit`;
	}

	get isGlobal() {
		return !this.receivingRulesGroup || this.receivingRulesGroup.conditions.length === 0;
	}

	get isWorkAnniversary() {
		return this.type === CelebrationType.WorkAnniversary;
	}

	get isWellness() {
		return this.type === CelebrationType.Wellness;
	}

	get isCustom() {
		return this.type === CelebrationType.Custom;
	}
}

@Schema()
export class CelebrationDetail {
	@Field()
	@From('id')
	public id = '';

	@Field(String)
	@From('name')
	public name = '';

	@Field()
	@From('type')
	public type: CelebrationType = CelebrationType.Welcome;

	@Field()
	@From('giverId')
	public giverId = '';

	@Field(String)
	@From('headline')
	public headline = '';

	@Field()
	@From('amount')
	public amount: number | null = null;

	@Field()
	@From('includeBenefactors')
	public includeBenefactors = false;

	@Field(String)
	@From('reason')
	public reason = '';

	@Field()
	@From('imageUrl')
	public imageUrl: string | null = null;

	@Field()
	@From('iconUrl')
	public iconUrl: string | null = null;

	@Field()
	@From('state')
	public state: CelebrationState = 'active';

	@Field()
	@From('visibility')
	public visibility: CelebrationVisibility = CelebrationVisibility.Public;

	@Field([YearAmountMapping])
	@From('yearAmountMappings')
	public yearAmountMappings: YearAmountMapping[] = [];

	@Field(RuleGroup)
	@From('receivingRulesGroup')
	public receivingRulesGroup: RuleGroup | null = null;

	@Field(DateOnlySerializer)
	@From('celebrationDate')
	public celebrationDate: Date | null = null;

	get isCustom() {
		return this.type === CelebrationType.Custom;
	}

	get isInactive() {
		return this.state === 'inactive';
	}
}

// Builders
//
export const CelebrationBuilder = new Builder(Celebration, {
	id: inc(String),
	type: CelebrationType.Welcome,
	reason: "Welcome to the Bonusly team, @recipient! Here's +250",
	state: 'active',
	amount: 50,
	visibility: CelebrationVisibility.Public,
	receivingRulesGroup: use(RuleGroupBuilder),
})
	.as('global', {
		receivingRulesGroup: null,
	})
	.as('birthday', {
		type: CelebrationType.Birthday,
		reason: '@recipient here is +2500 for your birthday',
	})
	.as('workAnniversary', {
		type: CelebrationType.WorkAnniversary,
		reason: '@recipient here is +2500 for your work anniversary',
	})
	.as('newHire', {
		type: CelebrationType.NewHire,
		reason: '@recipient here is +2500 for your new hire',
	})
	.as('welcome', {
		type: CelebrationType.Welcome,
		reason: '@recipient here is +250 for your welcome',
	});

export const CelebrationDetailBuilder = new Builder(CelebrationDetail, {
	id: inc(String),
	type: CelebrationType.Welcome,
	reason: "Welcome to the Bonusly team, @recipient! Here's +250",
	state: 'active',
	amount: 50,
	visibility: CelebrationVisibility.Public,
	receivingRulesGroup: use(RuleGroupBuilder),
})
	.as('global', {
		receivingRulesGroup: null,
	})
	.as('birthday', {
		type: CelebrationType.Birthday,
		reason: '@recipient here is +2500 for your birthday',
	})
	.as('workAnniversary', {
		type: CelebrationType.WorkAnniversary,
		reason: '@recipient here is +2500 for your work anniversary',
	})
	.as('newHire', {
		type: CelebrationType.NewHire,
		reason: '@recipient here is +2500 for your new hire',
	})
	.as('welcome', {
		type: CelebrationType.Welcome,
		reason: '@recipient here is +250 for your welcome',
	})
	.as('custom', {
		type: CelebrationType.Custom,
		reason: '@recipient here is +250 for your custom celebration',
	});
